import { useState, type ChangeEvent } from 'react';
import { useAuth } from '@/app/providers/AuthProvider';
import { currentThemeId, setCustomTheme, setTheme } from './themes';
import { saveThemePreference } from './api';

/**
 * The shared "pick a theme" behavior used by both the compact `ThemeSwitcher`
 * rail and the full grid in the Locker. Selecting a theme retints the page live
 * + persists to localStorage; when signed in it also mirrors to
 * `profiles.skin_id`. The custom upload reads a file into a per-device data URL.
 *
 * Kept in one place so the two surfaces can never drift on how a theme is set.
 */
export function useThemeSelection() {
  const { user } = useAuth();
  const [activeId, setActiveId] = useState(currentThemeId());

  function pick(id: string) {
    setTheme(id);
    setActiveId(id);
    if (user) void saveThemePreference(user.id, id);
  }

  function onUpload(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setCustomTheme(String(reader.result));
      setActiveId('custom');
    };
    reader.readAsDataURL(file);
  }

  return { activeId, pick, onUpload };
}
